import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Chip, CircularProgress, IconButton, Slider, Stack, TextField, Typography } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import Replay from '@mui/icons-material/Replay';
import TouchAppOutlined from '@mui/icons-material/TouchAppOutlined';
import LabelOutlined from '@mui/icons-material/LabelOutlined';
import CategoryOutlined from '@mui/icons-material/CategoryOutlined';
import Tune from '@mui/icons-material/Tune';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import RouteOutlined from '@mui/icons-material/RouteOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import ReplyOutlined from '@mui/icons-material/ReplyOutlined';
import FullscreenOutlined from '@mui/icons-material/FullscreenOutlined';
import { useTranslate } from 'react-admin';
import { blockInteractionService, type BlockInteractionResult, type DiscussionPost } from '../../../../services/blockInteractionService';
import { useMediaObjectUrl } from '../../../../hooks/useMediaObjectUrl';
import { useBlockInteraction } from './BlockInteractionContext';

/* ───────────────────────── shared ───────────────────────── */

const Head: React.FC<{ icon: React.ReactNode; title: string; right?: React.ReactNode }> = ({ icon, title, right }) => (
    <div className="oa-bp-card-head">
        <span className="oa-bp-icon">{icon}</span>
        <span className="oa-bp-grow">{title}</span>
        {right}
    </div>
);

const ResultBanner: React.FC<{ result: BlockInteractionResult | null; explanation?: string }> = ({ result, explanation }) => {
    const translate = useTranslate();
    if (!result || result.isCorrect === null) return null;
    return (
        <Alert severity={result.isCorrect ? 'success' : 'warning'} icon={result.isCorrect ? <CheckCircle fontSize="inherit" /> : <Cancel fontSize="inherit" />} sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {result.isCorrect ? translate('blockPlayer.result.correct') : translate('blockPlayer.result.incorrect', { percent: Math.round(result.scorePercent ?? 0) })}
            </Typography>
            {result.feedback && <Typography variant="body2">{result.feedback}</Typography>}
            {explanation && <Typography variant="body2" sx={{ mt: 0.5 }}>{explanation}</Typography>}
        </Alert>
    );
};

function useSubmit(blockId: string) {
    const { lessonId, readOnly, onInteraction } = useBlockInteraction();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<BlockInteractionResult | null>(null);
    const translate = useTranslate();

    const submit = useCallback(async (response: Record<string, unknown>) => {
        if (lessonId === null || readOnly) return null;
        setBusy(true);
        setError(null);
        try {
            const r = await blockInteractionService.submit(lessonId, blockId, response);
            setResult(r);
            onInteraction?.(blockId, r);
            return r;
        } catch {
            setError(translate('blockPlayer.result.error'));
            return null;
        } finally {
            setBusy(false);
        }
    }, [lessonId, readOnly, blockId, onInteraction, translate]);

    const reset = useCallback(() => { setResult(null); setError(null); }, []);
    return { submit, busy, error, result, reset, canSubmit: lessonId !== null && !readOnly };
}

const PreviewHint: React.FC = () => {
    const translate = useTranslate();
    return <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{translate('blockPlayer.previewHint')}</Typography>;
};

/* ───────────────────────── Hotspot (exploration) ───────────────────────── */

interface HotspotArea { x: number; y: number; width: number; height: number; label?: string; description?: string }

export const HotspotPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const areas = (Array.isArray(data.areas) ? data.areas : []) as HotspotArea[];
    const src = useMediaObjectUrl((data.imageSrc as string) || null);
    const { submit, canSubmit } = useSubmit(blockId);
    const [active, setActive] = useState<number | null>(null);
    const [revealed, setRevealed] = useState<Set<number>>(new Set());
    const recorded = useRef(false);

    const reveal = (i: number) => {
        setActive(i);
        setRevealed((prev) => {
            const next = new Set(prev).add(i);
            if (next.size === areas.length && !recorded.current && canSubmit) {
                recorded.current = true;
                void submit({ revealed: Array.from(next) });
            }
            return next;
        });
    };

    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="hotspot">
            <Head icon={<TouchAppOutlined fontSize="small" />} title={translate('blockPlayer.hotspot.title')} right={<Typography variant="caption" color="text.secondary">{translate('blockPlayer.hotspot.progress', { done: revealed.size, total: areas.length })}</Typography>} />
            <div className="oa-bp-card-body">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translate('blockPlayer.hotspot.hint')}</Typography>
                {src && (
                    <div className="oa-bp-stage">
                        <img src={src} alt={translate('blockPlayer.hotspot.title')} className="oa-bp-stage-img" />
                        {areas.map((a, i) => (
                            <button key={i} type="button" className={`oa-bp-stage-area${active === i ? ' is-active' : ''}${revealed.has(i) ? ' is-revealed' : ''}`} style={{ left: `${a.x}%`, top: `${a.y}%`, width: `${a.width}%`, height: `${a.height}%` }} onClick={() => reveal(i)} aria-label={a.label || translate('blockPlayer.hotspot.area', { n: i + 1 })}>
                                <span>{i + 1}</span>
                            </button>
                        ))}
                    </div>
                )}
                {active !== null && areas[active] && (
                    <Alert severity="info" sx={{ mt: 1.5 }} data-testid="hotspot-detail">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{areas[active].label || translate('blockPlayer.hotspot.area', { n: active + 1 })}</Typography>
                        {areas[active].description && <Typography variant="body2">{areas[active].description}</Typography>}
                    </Alert>
                )}
                {revealed.size === areas.length && areas.length > 0 && typeof data.explanation === 'string' && data.explanation && <Alert severity="success" sx={{ mt: 1.5 }}>{data.explanation}</Alert>}
            </div>
        </div>
    );
};

/* ───────────────────────── Labeling (graded) ───────────────────────── */

export const LabelingPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const labels = (Array.isArray(data.labels) ? data.labels : []) as { x: number; y: number; text?: string }[];
    const src = useMediaObjectUrl((data.imageSrc as string) || null);
    const { submit, busy, error, result, reset, canSubmit } = useSubmit(blockId);
    const [answers, setAnswers] = useState<string[]>(() => labels.map(() => ''));
    const solution = result?.solution?.labels;
    const isCorrect = (i: number) => (solution ? answers[i].trim().toLowerCase() === (solution[i] ?? '').trim().toLowerCase() : null);

    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="labeling">
            <Head icon={<LabelOutlined fontSize="small" />} title={translate('blockPlayer.labeling.title')} />
            <div className="oa-bp-card-body">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translate('blockPlayer.labeling.hint')}</Typography>
                {src && (
                    <div className="oa-bp-stage">
                        <img src={src} alt={translate('blockPlayer.labeling.title')} className="oa-bp-stage-img" />
                        {labels.map((l, i) => <div key={i} className={`oa-bp-stage-pin${isCorrect(i) === true ? ' is-correct' : isCorrect(i) === false ? ' is-wrong' : ''}`} style={{ left: `${l.x}%`, top: `${l.y}%` }}>{i + 1}</div>)}
                    </div>
                )}
                <Stack spacing={1} sx={{ mt: 1.5 }}>
                    {labels.map((_, i) => (
                        <Stack key={i} direction="row" spacing={1} alignItems="center">
                            <span className="oa-bp-index">{i + 1}</span>
                            <TextField size="small" fullWidth value={answers[i]} disabled={!!result} onChange={(e) => setAnswers((prev) => prev.map((a, k) => (k === i ? e.target.value : a)))} inputProps={{ 'aria-label': translate('blockPlayer.labeling.answer', { n: i + 1 }) }} error={isCorrect(i) === false} />
                            {solution && isCorrect(i) === false && <Typography variant="caption" color="text.secondary">({solution[i]})</Typography>}
                        </Stack>
                    ))}
                </Stack>
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                <ResultBanner result={result} explanation={data.explanation as string | undefined} />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    {!result ? (
                        <Button variant="contained" size="small" disabled={busy || !canSubmit || answers.some((a) => !a.trim())} onClick={() => void submit({ answers })}>{busy ? <CircularProgress size={16} /> : translate('blockPlayer.check')}</Button>
                    ) : (
                        <Button size="small" startIcon={<Replay />} onClick={() => { reset(); setAnswers(labels.map(() => '')); }}>{translate('blockPlayer.retry')}</Button>
                    )}
                </Stack>
                {!canSubmit && <PreviewHint />}
            </div>
        </div>
    );
};

/* ───────────────────────── Sorting (graded) ───────────────────────── */

export const SortingPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const categories = (Array.isArray(data.categories) ? data.categories : []) as { label: string; items?: string[] }[];
    // Learner payload: flat `items` with author index (answer key stripped); author preview: derive from categories.
    const items = useMemo<{ index: number; text: string }[]>(() => {
        if (Array.isArray(data.items)) return data.items as { index: number; text: string }[];
        const flat: { index: number; text: string }[] = [];
        categories.forEach((c) => (c.items ?? []).forEach((t) => { if (t.trim()) flat.push({ index: flat.length, text: t }); }));
        return flat;
    }, [data.items, categories]);
    const { submit, busy, error, result, reset, canSubmit } = useSubmit(blockId);
    const [placements, setPlacements] = useState<Record<number, number>>({});
    const [dragging, setDragging] = useState<number | null>(null);
    const correct = result?.solution?.correctCategories;

    const place = (itemIndex: number, cat: number | null) => {
        if (result) return;
        setPlacements((prev) => { const next = { ...prev }; if (cat === null) delete next[itemIndex]; else next[itemIndex] = cat; return next; });
    };
    const unplaced = items.filter((it) => placements[it.index] === undefined);
    const allPlaced = items.length > 0 && unplaced.length === 0;

    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="sorting">
            <Head icon={<CategoryOutlined fontSize="small" />} title={translate('blockPlayer.sorting.title')} />
            <div className="oa-bp-card-body">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translate('blockPlayer.sorting.hint')}</Typography>
                {unplaced.length > 0 && (
                    <div className="oa-bp-sorting-pool" data-testid="sorting-pool">
                        {unplaced.map((it) => (
                            <div key={it.index} className="oa-bp-sorting-item" draggable={!result} onDragStart={() => setDragging(it.index)} onDragEnd={() => setDragging(null)}>
                                <span>{it.text}</span>
                                <Stack direction="row" spacing={0.25}>
                                    {categories.map((c, ci) => <Button key={ci} size="small" variant="text" sx={{ minWidth: 0, px: 0.75 }} onClick={() => place(it.index, ci)} aria-label={translate('blockPlayer.sorting.placeIn', { category: c.label })}>{ci + 1}</Button>)}
                                </Stack>
                            </div>
                        ))}
                    </div>
                )}
                <div className="oa-bp-sorting-grid" style={{ gridTemplateColumns: `repeat(${Math.min(categories.length, 3)}, minmax(0, 1fr))` }}>
                    {categories.map((c, ci) => (
                        <div key={ci} className="oa-bp-sorting-cat" onDragOver={(e) => e.preventDefault()} onDrop={() => { if (dragging !== null) place(dragging, ci); setDragging(null); }} data-testid={`sorting-cat-${ci}`}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{ci + 1}. {c.label}</Typography>
                            {items.filter((it) => placements[it.index] === ci).map((it) => {
                                const state = correct ? (correct[it.index] === ci ? 'is-correct' : 'is-wrong') : '';
                                return (
                                    <div key={it.index} className={`oa-bp-sorting-item ${state}`}>
                                        <span>{it.text}</span>
                                        {!result && <IconButton size="small" onClick={() => place(it.index, null)} aria-label={translate('blockPlayer.sorting.remove')}><Cancel fontSize="inherit" /></IconButton>}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                <ResultBanner result={result} explanation={data.explanation as string | undefined} />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    {!result ? (
                        <Button variant="contained" size="small" disabled={!allPlaced || busy || !canSubmit} onClick={() => void submit({ placements: Object.fromEntries(Object.entries(placements).map(([k, v]) => [String(k), v])) })}>{busy ? <CircularProgress size={16} /> : translate('blockPlayer.check')}</Button>
                    ) : (
                        <Button size="small" startIcon={<Replay />} onClick={() => { reset(); setPlacements({}); }}>{translate('blockPlayer.retry')}</Button>
                    )}
                </Stack>
                {!canSubmit && <PreviewHint />}
            </div>
        </div>
    );
};

/* ───────────────────────── Slider scale ───────────────────────── */

export const SliderScalePlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const min = typeof data.minValue === 'number' ? data.minValue : 1;
    const max = typeof data.maxValue === 'number' && data.maxValue > min ? data.maxValue : 10;
    const step = typeof data.step === 'number' && data.step > 0 ? data.step : 1;
    const { submit, busy, result, canSubmit } = useSubmit(blockId);
    const [value, setValue] = useState<number>(Math.floor((min + max) / 2));

    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="slider_scale">
            <Head icon={<Tune fontSize="small" />} title={translate('blockPlayer.sliderScale.title')} />
            <div className="oa-bp-card-body">
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{data.question as string}</Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="caption">{min}</Typography>
                    <Slider value={value} min={min} max={max} step={step} marks valueLabelDisplay="auto" disabled={!!result} onChange={(_, v) => setValue(v as number)} aria-label={data.question as string} />
                    <Typography variant="caption">{max}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">{data.minLabel as string}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} data-testid="slider-value">{value}</Typography>
                    <Typography variant="caption" color="text.secondary">{data.maxLabel as string}</Typography>
                </Stack>
                {!result ? (
                    <Button variant="contained" size="small" sx={{ mt: 1.5 }} disabled={busy || !canSubmit} onClick={() => void submit({ value })}>{translate('blockPlayer.sliderScale.submit')}</Button>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{translate('blockPlayer.sliderScale.saved')}</Typography>
                )}
                {!canSubmit && <PreviewHint />}
            </div>
        </div>
    );
};

/* ───────────────────────── Discussion ───────────────────────── */

export const DiscussionPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const { lessonId, readOnly } = useBlockInteraction();
    const live = lessonId !== null && !readOnly;
    const allowReplies = data.allowReplies !== false;
    const [posts, setPosts] = useState<DiscussionPost[]>([]);
    const [canModerate, setCanModerate] = useState(false);
    const [body, setBody] = useState('');
    const [replyTo, setReplyTo] = useState<number | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!live) return;
        try {
            const r = await blockInteractionService.discussion(lessonId!, blockId);
            setPosts(r.posts);
            setCanModerate(r.canModerate);
        } catch { /* thread stays empty */ }
    }, [live, lessonId, blockId]);
    useEffect(() => { void load(); }, [load]);

    const send = async () => {
        if (!live || !body.trim()) return;
        setBusy(true);
        setError(null);
        try {
            await blockInteractionService.post(lessonId!, blockId, body.trim(), replyTo);
            setBody('');
            setReplyTo(null);
            await load();
        } catch (e) {
            const d = (e as { response?: { data?: { error?: string } } })?.response?.data;
            setError(d?.error ?? translate('blockPlayer.discussion.error'));
        } finally {
            setBusy(false);
        }
    };
    const act = async (fn: () => Promise<unknown>) => { try { await fn(); await load(); } catch { setError(translate('blockPlayer.discussion.error')); } };

    const roots = posts.filter((p) => p.parentId === null);
    const repliesOf = (id: number) => posts.filter((p) => p.parentId === id);

    const Post: React.FC<{ post: DiscussionPost; reply?: boolean }> = ({ post, reply }) => (
        <div className={`oa-bp-post${reply ? ' is-reply' : ''}${post.status !== 'published' ? ' is-pending' : ''}`} data-testid={`post-${post.id}`}>
            <div className="oa-bp-post-head">
                <strong>{post.authorName ?? translate('blockPlayer.discussion.anonymous')}</strong>
                <Typography variant="caption" color="text.secondary">{new Date(post.createdAt).toLocaleString()}</Typography>
                {post.status === 'pending' && <Chip size="small" label={translate('blockPlayer.discussion.pending')} />}
                {post.status === 'rejected' && <Chip size="small" color="error" variant="outlined" label={translate('blockPlayer.discussion.rejected')} />}
                <span className="oa-bp-grow" />
                {canModerate && post.status === 'pending' && <Button size="small" onClick={() => void act(() => blockInteractionService.moderate(lessonId!, blockId, post.id, 'published'))}>{translate('blockPlayer.discussion.approve')}</Button>}
                {canModerate && post.status === 'pending' && <Button size="small" color="inherit" onClick={() => void act(() => blockInteractionService.moderate(lessonId!, blockId, post.id, 'rejected'))}>{translate('blockPlayer.discussion.reject')}</Button>}
                {!reply && allowReplies && <IconButton size="small" onClick={() => setReplyTo(post.id)} aria-label={translate('blockPlayer.discussion.reply')}><ReplyOutlined fontSize="inherit" /></IconButton>}
                {(post.isMine || canModerate) && <IconButton size="small" onClick={() => void act(() => blockInteractionService.deletePost(lessonId!, blockId, post.id))} aria-label={translate('blockPlayer.discussion.delete')}><DeleteOutline fontSize="inherit" /></IconButton>}
            </div>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{post.body}</Typography>
        </div>
    );

    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="discussion">
            <Head icon={<ForumOutlined fontSize="small" />} title={translate('blockPlayer.discussion.title')} right={<Typography variant="caption" color="text.secondary">{translate('blockPlayer.discussion.count', { smart_count: posts.length })}</Typography>} />
            <div className="oa-bp-card-body">
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>{data.prompt as string}</Typography>
                <Stack spacing={1}>
                    {roots.map((p) => (
                        <div key={p.id}>
                            <Post post={p} />
                            {repliesOf(p.id).map((r) => <Post key={r.id} post={r} reply />)}
                        </div>
                    ))}
                    {live && roots.length === 0 && <Typography variant="body2" color="text.secondary">{translate('blockPlayer.discussion.empty')}</Typography>}
                </Stack>
                {live ? (
                    <Stack spacing={1} sx={{ mt: 2 }}>
                        {replyTo !== null && <Typography variant="caption" color="text.secondary">{translate('blockPlayer.discussion.replyingTo')} <Button size="small" onClick={() => setReplyTo(null)}>{translate('ra.action.cancel')}</Button></Typography>}
                        <TextField size="small" multiline minRows={2} fullWidth value={body} onChange={(e) => setBody(e.target.value)} placeholder={translate('blockPlayer.discussion.placeholder')} inputProps={{ maxLength: 4000, 'aria-label': translate('blockPlayer.discussion.placeholder') }} />
                        {error && <Alert severity="error">{error}</Alert>}
                        <Button variant="contained" size="small" sx={{ alignSelf: 'flex-start' }} disabled={busy || !body.trim()} onClick={() => void send()}>{busy ? <CircularProgress size={16} /> : translate('blockPlayer.discussion.send')}</Button>
                        {data.moderationRequired === true && <Typography variant="caption" color="text.secondary">{translate('blockPlayer.discussion.moderationHint')}</Typography>}
                    </Stack>
                ) : <PreviewHint />}
            </div>
        </div>
    );
};

/* ───────────────────────── Branching ───────────────────────── */

interface BranchNode { id: string; question: string; options: { label: string; nextNodeId?: string }[] }

export const BranchingPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const nodes = (Array.isArray(data.nodes) ? data.nodes : []) as BranchNode[];
    const startId = (data.startNodeId as string) || nodes[0]?.id;
    const { submit, canSubmit } = useSubmit(blockId);
    const [current, setCurrent] = useState<string | undefined>(startId);
    const [path, setPath] = useState<{ nodeId: string; optionIndex: number; label: string }[]>([]);
    const [finished, setFinished] = useState(false);
    const node = nodes.find((n) => n.id === current);

    const choose = (i: number) => {
        if (!node) return;
        const nextPath = [...path, { nodeId: node.id, optionIndex: i, label: node.options[i].label }];
        setPath(nextPath);
        const next = node.options[i].nextNodeId;
        if (next && nodes.some((n) => n.id === next)) {
            setCurrent(next);
        } else {
            setFinished(true);
            if (canSubmit) void submit({ path: nextPath.map((p) => ({ nodeId: p.nodeId, optionIndex: p.optionIndex })) });
        }
    };
    const restart = () => { setCurrent(startId); setPath([]); setFinished(false); };

    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="branching">
            <Head icon={<AccountTreeOutlined fontSize="small" />} title={translate('blockPlayer.branching.title')} right={path.length > 0 ? <Typography variant="caption" color="text.secondary">{translate('blockPlayer.branching.step', { n: path.length + (finished ? 0 : 1) })}</Typography> : undefined} />
            <div className="oa-bp-card-body">
                {path.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                        {path.map((p, i) => <Chip key={i} size="small" label={p.label} />)}
                    </Stack>
                )}
                {!finished && node ? (
                    <>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{node.question}</Typography>
                        <Stack spacing={0.75}>
                            {node.options.map((o, i) => <Button key={i} variant="outlined" sx={{ justifyContent: 'flex-start', textTransform: 'none' }} onClick={() => choose(i)}>{o.label}</Button>)}
                        </Stack>
                    </>
                ) : (
                    <Alert severity="success" data-testid="branching-done">{translate('blockPlayer.branching.done', { smart_count: path.length })}</Alert>
                )}
                <Button size="small" startIcon={<Replay />} sx={{ mt: 1.5 }} onClick={restart}>{translate('blockPlayer.branching.restart')}</Button>
                {!canSubmit && <PreviewHint />}
            </div>
        </div>
    );
};

/* ───────────────────────── Simulation (graded) ───────────────────────── */

interface SimChoice { label: string; feedback?: string; nextStepId?: string; isCorrect?: boolean }
interface SimStep { id: string; text: string; choices: SimChoice[] }

export const SimulationPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const steps = (Array.isArray(data.steps) ? data.steps : []) as SimStep[];
    const { submit, busy, result, reset, canSubmit } = useSubmit(blockId);
    const [current, setCurrent] = useState<string | undefined>(steps[0]?.id);
    const [history, setHistory] = useState<{ stepId: string; choiceIndex: number; label: string }[]>([]);
    const [picked, setPicked] = useState<number | null>(null);
    const [finished, setFinished] = useState(false);
    const step = steps.find((s) => s.id === current);
    const feedbackFor = (stepId: string, choiceIndex: number) => result?.solution?.steps?.[stepId]?.find((c) => c.index === choiceIndex);
    // Learner payload has isCorrect/feedback stripped; the author preview still has them.
    const localChoice = step && picked !== null ? step.choices[picked] : null;

    const next = () => {
        if (!step || picked === null) return;
        const nextHistory = [...history, { stepId: step.id, choiceIndex: picked, label: step.choices[picked].label }];
        setHistory(nextHistory);
        setPicked(null);
        const to = step.choices[picked].nextStepId;
        if (to && steps.some((s) => s.id === to)) {
            setCurrent(to);
        } else {
            setFinished(true);
            if (canSubmit) void submit({ path: nextHistory.map((h) => ({ stepId: h.stepId, choiceIndex: h.choiceIndex })) });
        }
    };
    const restart = () => { reset(); setCurrent(steps[0]?.id); setHistory([]); setPicked(null); setFinished(false); };

    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="simulation">
            <Head icon={<RouteOutlined fontSize="small" />} title={translate('blockPlayer.simulation.title')} right={history.length > 0 ? <Typography variant="caption" color="text.secondary">{translate('blockPlayer.simulation.decision', { n: history.length + (finished ? 0 : 1) })}</Typography> : undefined} />
            <div className="oa-bp-card-body">
                {typeof data.scenario === 'string' && data.scenario && <Alert severity="info" icon={false} sx={{ mb: 1.5 }}>{data.scenario}</Alert>}
                {!finished && step ? (
                    <>
                        <Typography variant="body1" sx={{ mb: 1 }}>{step.text}</Typography>
                        {picked === null ? (
                            <Stack spacing={0.75}>
                                {step.choices.map((c, i) => <Button key={i} variant="outlined" sx={{ justifyContent: 'flex-start', textTransform: 'none' }} onClick={() => setPicked(i)} data-testid={`sim-choice-${step.id}-${i}`}>{c.label}</Button>)}
                            </Stack>
                        ) : (
                            <>
                                <Alert severity={localChoice?.isCorrect === undefined ? 'info' : localChoice.isCorrect ? 'success' : 'warning'} sx={{ mb: 1 }} data-testid="sim-feedback">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{localChoice?.label}</Typography>
                                    {localChoice?.feedback && <Typography variant="body2">{localChoice.feedback}</Typography>}
                                </Alert>
                                <Button variant="contained" size="small" onClick={next} data-testid="sim-continue">{localChoice?.nextStepId ? translate('blockPlayer.simulation.continue') : translate('blockPlayer.simulation.finish')}</Button>
                            </>
                        )}
                    </>
                ) : finished ? (
                    <div data-testid="sim-result">
                        {busy && <CircularProgress size={18} />}
                        {result ? <ResultBanner result={result} /> : !canSubmit ? <Alert severity="info">{translate('blockPlayer.simulation.done')}</Alert> : null}
                        <Stack spacing={0.5} sx={{ mt: 1.5 }}>
                            {history.map((h, i) => {
                                const fb = feedbackFor(h.stepId, h.choiceIndex);
                                return (
                                    <div key={i} className={`oa-bp-orderitem${fb ? (fb.isCorrect ? ' is-correct' : ' is-wrong') : ''}`}>
                                        <span className="oa-bp-index">{i + 1}</span>
                                        <div>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{h.label}</Typography>
                                            {fb?.feedback && <Typography variant="body2" color="text.secondary">{fb.feedback}</Typography>}
                                        </div>
                                    </div>
                                );
                            })}
                        </Stack>
                    </div>
                ) : null}
                <Button size="small" startIcon={<Replay />} sx={{ mt: 1.5 }} onClick={restart} data-testid="sim-restart">{translate('blockPlayer.simulation.restart')}</Button>
                {!canSubmit && <PreviewHint />}
            </div>
        </div>
    );
};

/* ───────────────────────── SCORM embed ───────────────────────── */

export const ScormEmbedPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const { submit, canSubmit } = useSubmit(blockId);
    const uuid = (data.mediaFileUuid as string) || ((data.packageUrl as string) || '').match(/\/api\/media\/([0-9a-f-]{36})/i)?.[1] || '';
    const apiBase = process.env.REACT_APP_API_URL || '';
    const src = uuid ? `${apiBase}/api/scorm/player/${uuid}` : '';
    const containerRef = useRef<HTMLDivElement>(null);
    const [status, setStatus] = useState<string | null>(null);
    const done = useRef(false);

    useEffect(() => {
        if (!src) return undefined;
        const onMessage = (e: MessageEvent) => {
            const d = e.data;
            if (!d || d.type !== 'scorm') return;
            const key = d.element ?? d.key;
            const value = String(d.value ?? '');
            const completed = (key === 'cmi.core.lesson_status' && ['completed', 'passed'].includes(value)) || (key === 'cmi.completion_status' && value === 'completed') || (key === 'cmi.success_status' && value === 'passed') || ['LMSFinish', 'Terminate'].includes(d.action);
            if (completed && !done.current) {
                done.current = true;
                setStatus(value || 'completed');
                if (canSubmit) void submit({ completed: true, status: value || 'completed' });
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [src, canSubmit, submit]);

    if (!src) {
        return <Alert severity="info" variant="outlined" sx={{ my: 1 }}>{translate('blockPlayer.scorm.empty')}</Alert>;
    }
    return (
        <div ref={containerRef} className="oa-bp-card oa-bp-flush" data-block-type="scorm_embed">
            <Head icon={<Inventory2Outlined fontSize="small" />} title={(data.title as string) || translate('blockPlayer.scorm.title')} right={<Stack direction="row" spacing={1} alignItems="center">{status && <Chip size="small" color="success" label={translate('blockPlayer.scorm.completed')} />}<Chip size="small" label={`SCORM ${(data.version as string) || '1.2'}`} /><IconButton size="small" aria-label={translate('blockPlayer.scorm.fullscreen')} onClick={() => void containerRef.current?.requestFullscreen?.().catch(() => undefined)}><FullscreenOutlined fontSize="small" /></IconButton></Stack>} />
            <iframe src={src} title={(data.title as string) || 'SCORM'} className="oa-bp-embed-frame" allow="fullscreen" allowFullScreen />
        </div>
    );
};

/* ───────────────────────── H5P ───────────────────────── */

export const H5pPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const { submit, canSubmit } = useSubmit(blockId);
    const contentId = data.contentId ? String(data.contentId) : '';
    const token = localStorage.getItem('token') || '';
    const src = contentId ? `/h5p/${contentId}/play?token=${encodeURIComponent(token)}` : ((data.embedUrl as string) || '');
    const [height, setHeight] = useState(500);
    const done = useRef(false);

    useEffect(() => {
        const onMessage = (e: MessageEvent) => {
            const d = e.data;
            if (d?.type === 'h5p-resize' && typeof d.height === 'number') setHeight(Math.max(200, Math.min(5000, d.height)));
            if (d?.type === 'h5p-xapi') {
                const verb = String(d.statement?.verb?.id ?? d.verb ?? '').toLowerCase();
                if ((verb.includes('completed') || (verb.includes('answered') && d.statement?.result?.completion)) && !done.current) {
                    done.current = true;
                    if (canSubmit) void submit({ completed: true, score: d.statement?.result?.score?.scaled ?? null });
                }
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [canSubmit, submit]);

    if (!src) {
        return <Alert severity="info" variant="outlined" sx={{ my: 1 }}>{translate('blockPlayer.h5p.empty')}</Alert>;
    }
    return (
        <div className="oa-bp-card oa-bp-flush" data-block-type="h5p">
            <Head icon={<ExtensionOutlined fontSize="small" />} title={(data.title as string) || translate('blockPlayer.h5p.title')} />
            <iframe src={src} title={(data.title as string) || 'H5P'} className="oa-bp-embed-frame" style={{ height }} allow="fullscreen" allowFullScreen />
        </div>
    );
};
